<template>
  <div id="post-item-collection" class="flex-1">

    <div v-if="initialLoadFailed" class="col-span-full flex flex-col items-center justify-center py-20 px-6 text-center">
      <svg class="w-16 h-16 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
      </svg>
      <p class="text-gray-600 text-lg mb-6">{{ initialErrorMessage }}</p>
      <button @click="retryInitialLoad" class="btn btn-primary px-8 py-2 bg-primary text-white rounded-full">
        {{ $t('home_post_collection.retry') }}
      </button>
    </div>

    <template v-else-if="loading && items.length === 0 && pinned.length === 0">
      <div v-for="n in skeletonCount" :key="`sk-${n}`" class="post-item-skeleton animate-pulse">
        <div class="skeleton-image aspect-square rounded-lg bg-gray-200"></div>
        <div class="p-2 py-3 space-y-2">
          <div class="h-4 bg-gray-200 rounded w-3/4"></div>
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 bg-gray-200 rounded-full flex-shrink-0"></div>
            <div class="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="pinned && pinned.length > 0" v-for="(post, index) in pinned" :key="'pinned-' + post.id"
           :ref="(el) => bindHomeImpression('pinned-' + post.id, el, post, index)">
        <post-item :post="post" :list-name="homeListName"></post-item>
      </div>

      <div v-if="items && items.length > 0" v-for="(post, index) in items" :key="post.id"
           :ref="(el) => bindHomeImpression(post.id, el, post, pinned.length + index)">
        <post-item :post="post" :list-name="homeListName"></post-item>
      </div>
      <div v-else-if="itemsNotFound && postFeed" class="text-center post-item-not-found">
        <div class="icon"></div>
        <div class="title">{{ $t('home_post_collection.no_post_warning') }}</div>
      </div>
      <div v-else-if="itemsNotFound" class="text-center post-item-not-found">
        <div class="icon"></div>
        <div class="title">
          {{ $t('home_post_collection.no_matching_post') }}
        </div>
      </div>
    </template>

    <template v-if="autoloadMode">
      <div v-if="hasNoMoreData" class="col-span-full text-center py-4 text-sm text-gray-400">
        {{ $t('home_post_collection.no_more_posts') }}
      </div>

      <div v-else-if="loading && items.length > 0" class="col-span-full text-center post-item-loading">
        <img src="../../images/icons/loading.png" class="w-6 h-6 mx-auto icon loading-icon"/>
      </div>
    </template>

  </div>
</template>

<script>
import { setWithExpiry, getWithExpiry } from "../utils/localCache";
import { useInfiniteScroll } from "./post_index/useInfiniteScroll";
import { useModuleImpression } from "../utils/useModuleImpression";

const homeItemSearchPath = "/api/v1/home/posts";
const postItemSearchPath = "/api/v1/posts";
const CACHE_EXPIRY_MS = 120000;
const REQUEST_TIMEOUT_MS = 10000;

// 後台 PostCollection 的 title（見 app/controllers/home_controller.rb）對應曝光事件的 module 名稱。
// Trending 區塊沒有固定 collection（postCollection 是空字串），對不到就當 trending。
const HOME_COLLECTION_MODULE_MAP = {
  'PhrozenVerified-Pin': 'phrozen_verified',
  'NewbieFriendly-Pin': 'newbie_friendly',
  'Featured-Models-Pin': 'featured_models',
};

export default {
  props: ['postFeed', 'postCollection'],
  setup() {
    // 首頁一個元件會掛多次（各自對應不同 collection），但只有 trending 是無限捲動、
    // 張數夠多讓「每 12 張」取樣有意義；其他釘選區塊（phrozen_verified/featured_models/
    // newbie_friendly）固定只有 itemsCols(6) * itemsRows(2) = 12 張，取樣只會觸發 position 0
    // 這一筆，沒有分析意義，所以只在 trending 掛曝光追蹤，見 bindHomeImpression()。
    const { bindImpression } = useModuleImpression({ sampleEvery: 12 })
    return { bindImpression }
  },
  data() {
    return {
      itemsCols: 6,
      itemsRows: 2,
      itemsCount: 12,

      perPage: 24,
      currPage: 0,
      autoload: this.postCollection == '',
      autoloadMode: this.postCollection == '',
      skeletonCount: 12,

      cacheKey: 'post_cols_cache_key' + this.postCollection,

      loading: false,
      itemsNotFound: false,
      initialLoadFailed: false,
      initialErrorMessage: '',
      pinned: [],
      pinnedData: [],
      items: [],
      itemsData: [],
    };
  },
  mounted() {
    this.setupItemColsCount();

    if (this.autoload) {
      const cacheData = getWithExpiry(this.cacheKey);
      if (cacheData) {
        this.currPage = cacheData.page;
        this.items = cacheData.data;
        this.replaceSsrContent();
      } else {
        this.fetchPostItemData();
      }

      this._infiniteScroll = useInfiniteScroll({
        canLoad: () => this.autoload && !this.loading,
        onLoad: () => this.fetchPostItemData(),
      });
      this._infiniteScroll.setupScroll();
    } else {
      const cacheData = getWithExpiry(this.cacheKey);
      if (cacheData && cacheData.pinnedData && cacheData.itemsData) {
        this.pinnedData = cacheData.pinnedData;
        this.itemsData = cacheData.itemsData;
        this.applyGridSlice();
        this.replaceSsrContent();
      } else {
        this.fetchHomeItemData();
      }

      this._resizeHandler = () => {
        this.setupItemColsCount();
        this.applyGridSlice();
      };
      window.addEventListener("resize", this._resizeHandler);
    }
  },
  beforeUnmount() {
    if (this._infiniteScroll) this._infiniteScroll.cleanupScroll();
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
  },
  methods: {
    // 票 08：SSR 卡片跟 Vue 的掛載節點是分開的兄弟節點（見
    // home_post_collection.js：Vue 掛載到 container 裡新建的空節點，而不是
    // container 本身，所以 this.$el 的直接 parentElement 是那個掛載節點，還要
    // 再往上一層才會碰到裝著 .ssr-posts 的 .home-post-collection 容器，因此用
    // closest 往上找，不依賴固定的層數）。只有在 API 回應真的到位後才把 SSR
    // 卡片拿掉，避免掛載瞬間整片空白。找不到就代表已經被拿掉或本來就沒有 SSR
    // 卡片，不用做事。
    replaceSsrContent() {
      const collectionRoot = this.$el?.closest('.home-post-collection');
      const ssrNode = collectionRoot?.querySelector('.ssr-posts');
      if (ssrNode) ssrNode.remove();
    },

    bindHomeImpression(key, el, post, position) {
      if (this.homeImpressionModule !== 'trending') return;

      this.bindImpression(key, el, this.homeImpressionPayload(post, position));
    },

    homeImpressionPayload(post, position) {
      return {
        page_type: 'home',
        module: this.homeImpressionModule,
        landing_path: window.location.pathname,
        target_type: 'post',
        target_id: post.id,
        position,
      };
    },

    applyGridSlice() {
      const pinnedSlice = this.pinnedData.slice(0, this.itemsCount);
      const feedSlice = this.itemsData.slice(0, this.itemsCount - pinnedSlice.length);
      this.pinned = pinnedSlice;
      this.items = feedSlice;
    },

    retryInitialLoad() {
      this.initialLoadFailed = false;
      this.initialErrorMessage = '';
      this.pinned = [];
      this.items = [];
      this.currPage = 0;
      this.autoload = this.postCollection == '';

      if (this.autoload) {
        this.fetchPostItemData();
      } else {
        this.fetchHomeItemData();
      }
    },

    async fetchHomeItemData() {
      if (this.loading) return;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        this.loading = true;
        this.itemsNotFound = false;

        const response = await fetch(
          homeItemSearchPath +
            "?" +
            new URLSearchParams({
              kw: this.postFeed,
              post_collection_kw: this.postCollection,
              per_page: this.perPage
            }),
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        const pinnedItems = data["pinned_data"];
        const feedItems = data["data"];

        if (pinnedItems.length === 0 && feedItems.length === 0) {
          this.itemsNotFound = true;
        } else {
          this.pinnedData = pinnedItems;
          this.itemsData = feedItems;
          this.applyGridSlice();

          setWithExpiry(this.cacheKey, {
            params: `kw=${this.postFeed}&autoload=true`,
            data: this.items,
            page: this.currPage,
            pinnedData: this.pinnedData,
            itemsData: this.itemsData
          }, CACHE_EXPIRY_MS);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        this.initialLoadFailed = true;
        if (error.name === 'AbortError') {
          this.initialErrorMessage = this.$t('errors.request_timeout') || 'Request timeout. Please try again.';
        } else {
          this.initialErrorMessage = this.$t('errors.initial_load_failed') || 'Failed to load content. Please try again.';
        }
      } finally {
        // 成功或失敗都要拆 SSR 卡片：失敗時若留著，會和「載入失敗」面板同時出現（票 08 回歸修正）
        this.replaceSsrContent();
        this.loading = false;
      }
    },

    async fetchPostItemData() {
      if (this.loading) return;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      const targetPage = this.currPage + 1;

      try {
        this.loading = true;

        const requestParams = {
          'kw': this.postFeed,
          'per_page': this.perPage,
          'page': targetPage
        };

        const response = await fetch(
          postItemSearchPath + "?" + new URLSearchParams(requestParams),
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        const itemsData = data["data"];

        if (targetPage > data["pagination"].total_pages) {
          this.autoload = false;
        } else if (itemsData.length > 0) {
          this.items = [...this.items, ...itemsData];
          this.currPage = targetPage;
          this.trackViewItemList(itemsData, targetPage);

          setWithExpiry(this.cacheKey, {
            params: `kw=${this.postFeed}`,
            data: this.items,
            page: this.currPage
          }, CACHE_EXPIRY_MS);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        if (this.items.length === 0) {
          this.initialLoadFailed = true;
          if (error.name === 'AbortError') {
            this.initialErrorMessage = this.$t('errors.request_timeout') || 'Request timeout. Please try again.';
          } else {
            this.initialErrorMessage = this.$t('errors.initial_load_failed') || 'Failed to load content. Please try again.';
          }
        }
        this.autoload = false;
      } finally {
        // 成功或失敗都要拆 SSR 卡片：失敗時若留著，會和「載入失敗」面板同時出現（票 08 回歸修正）
        this.replaceSsrContent();
        this.loading = false;
      }
    },

    setupItemColsCount() {
      if (window.innerWidth >= 1920) {
        this.itemsCols = 6;
      } else if (window.innerWidth >= 1536) {
        this.itemsCols = 5;
      } else if (window.innerWidth >= 1024) {
        this.itemsCols = 4;
      } else if (window.innerWidth >= 780) {
        this.itemsCols = 3;
      } else if (window.innerWidth >= 705) {
        this.itemsCols = 2;
      } else {
        this.itemsCols = 1;
      }

      this.itemsCount = this.itemsCols === 1 ? 6 : this.itemsCols * this.itemsRows;
    },

    trackViewItemList(itemsData, pageNumber = 1, baseIndexOverride = null) {
      if (!window.trackingEvent || !itemsData?.length) return;

      const raw = this.homeListName || { id: 'home', name: 'home' };
      const listId = typeof raw === 'object' ? raw.id : raw;
      const listName = typeof raw === 'object' ? raw.name : raw;
      const baseIndex =
        typeof baseIndexOverride === 'number'
          ? baseIndexOverride
          : (pageNumber - 1) * this.perPage;

      window.trackingEvent.view_item_list({
        item_list_id: listId,
        item_list_name: listName,
        item_list_index: pageNumber,
        items: itemsData.map((item, index) => ({
          id: item.id,
          title: item.title,
          creator: item.creator?.brand_name,
          price: item.price,
          index: baseIndex + index,
        })),
      });
    },

  },
  computed: {
    hasNoMoreData() {
      return this.autoloadMode && this.items?.length > 0 && !this.autoload && !this.loading;
    },
    homeListName() {
      return this.postFeed ? 'home_' + this.postFeed : 'home';
    },
    homeImpressionModule() {
      return HOME_COLLECTION_MODULE_MAP[this.postCollection] || 'trending';
    },
  },
  watch: {
    hasNoMoreData(newVal) {
      if (newVal) {
        const noMoreDataElement = document.getElementById('global-no-more-data');
        if (noMoreDataElement) {
          noMoreDataElement.classList.remove('hidden');
        }
      }
    }
  },
};
</script>
